// MFA enrollment (mandatory) — shown to `admin` accounts that have zero
// verified TOTP factors. Distinct from mfa-challenge.tsx, which steps an
// *already-enrolled* factor up to AAL2 for the current session; this page
// walks a brand-new admin through enrolling their first factor before they
// can reach any authenticated route at all (see MfaGate.tsx).
//
// Sits OUTSIDE MfaGate (see App.tsx) so the gate can redirect here without an
// infinite loop, mirroring /auth/mfa-challenge.
//
// Reference: https://supabase.com/docs/guides/auth/auth-mfa
import { type FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Loader2, Shield } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";

interface EnrollData {
  factorId: string;
  qrCode: string;
  secret: string;
}

export function MfaEnrollPage() {
  const navigate = useNavigate();
  const [bootstrap, setBootstrap] = useState(true);
  const [enrollData, setEnrollData] = useState<EnrollData | null>(null);
  const [starting, setStarting] = useState(false);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      // Defensive: if this admin already has a verified TOTP factor (e.g. a
      // stale redirect, or enrolled from another tab in the meantime), don't
      // force a second enrollment — bounce home instead.
      const { data, error: listErr } = await supabase.auth.mfa.listFactors();
      if (!active) return;
      if (!listErr) {
        const verified = (data?.totp ?? []).some(
          (f) => f.status === "verified",
        );
        if (verified) {
          navigate("/dashboard", { replace: true });
          return;
        }
      }
      setBootstrap(false);
    })();
    return () => {
      active = false;
    };
  }, [navigate]);

  async function handleStartEnroll() {
    setStarting(true);
    setError(null);
    const { data, error: enrollErr } = await supabase.auth.mfa.enroll({
      factorType: "totp",
      friendlyName: "primary",
    });
    setStarting(false);

    if (enrollErr || !data) {
      setError(enrollErr?.message ?? "Could not start enrollment");
      return;
    }

    // Supabase returns both `qr_code` (SVG data URL ready for <img>) and
    // `uri` (otpauth://) on the enroll response.
    const totp = data.totp as {
      qr_code?: string;
      uri?: string;
      secret: string;
    };
    setEnrollData({
      factorId: data.id,
      qrCode: totp.qr_code ?? totp.uri ?? "",
      secret: totp.secret,
    });
    setCode("");
  }

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    if (!enrollData) return;
    if (code.trim().length !== 6) {
      setError("Enter the 6-digit code");
      return;
    }

    setVerifying(true);
    setError(null);
    const { error: verifyErr } = await supabase.auth.mfa.challengeAndVerify({
      factorId: enrollData.factorId,
      code: code.trim(),
    });
    setVerifying(false);

    if (verifyErr) {
      setError(verifyErr.message);
      return;
    }

    navigate("/dashboard", { replace: true });
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Set up two-factor authentication
          </CardTitle>
          <CardDescription>
            Admin accounts require an authenticator app before you can access
            cc-os. Scan the QR code below with Google Authenticator,
            1Password, Authy, or any TOTP app, then enter the 6-digit code it
            generates.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {bootstrap ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading
            </div>
          ) : !enrollData ? (
            <div className="space-y-4">
              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
              <Button onClick={() => void handleStartEnroll()} disabled={starting}>
                {starting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating QR
                  </>
                ) : (
                  "Set up authenticator app"
                )}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleVerify} className="space-y-5">
              <div className="flex flex-col items-center gap-3 rounded-md border bg-card p-6">
                {enrollData.qrCode ? (
                  <img
                    src={enrollData.qrCode}
                    alt="TOTP QR code"
                    width={200}
                    height={200}
                    className="h-[200px] w-[200px]"
                  />
                ) : (
                  <div className="flex h-[200px] w-[200px] items-center justify-center text-xs text-muted-foreground">
                    QR unavailable — use the secret below
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Or enter this manually:
                </p>
                <code className="block break-all rounded bg-muted px-2 py-1 text-[11px]">
                  {enrollData.secret}
                </code>
              </div>

              <div className="space-y-2">
                <Label htmlFor="totp-code">Authentication code</Label>
                <Input
                  id="totp-code"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoComplete="one-time-code"
                  autoFocus
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  placeholder="123456"
                  required
                />
              </div>

              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={verifying || code.length !== 6}
              >
                {verifying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying
                  </>
                ) : (
                  "Verify and continue"
                )}
              </Button>
            </form>
          )}

          <button
            type="button"
            onClick={() => void handleSignOut()}
            className="mt-4 block w-full text-center text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            Sign out
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
